import { getDefaultColors } from "../../../utils/colors";

/**
 * The category palette, kept apart from the cell that draws it.
 *
 * `CategoryCell` is a `"use client"` module, so a server component — or a Next.js app
 * route, which resolves `@texturehq/edges` through the `react-server` condition and
 * therefore against `dist/server.js` — cannot reach anything declared inside it. These
 * two are plain data derived from `getDefaultColors()`: no JSX, no hooks, no client
 * directive. Splitting them out is what lets `@texturehq/edges/server` re-export them,
 * which is what the boards table-cell planner needs to assign colour slots on the
 * server. The same split `deviceStateLabels` already has, for the same reason.
 */

/**
 * The swatch for a value with no slot of its own.
 *
 * Twelve categorical slots exist; a rendering with more distinct values than that
 * gives the overflow this one shared grey. Deliberately not a thirteenth colour and
 * deliberately not slot 1 again: a recycled colour asserts that two unrelated values
 * belong together, which is the exact failure a category swatch is supposed to
 * prevent. Grey says "not one of the twelve", which is true.
 */
export const CATEGORY_NEUTRAL_COLOR = "var(--color-text-subtle)";

/**
 * The categorical slots, as CSS custom-property references, in the palette's own
 * order: `CATEGORY_COLOR_TOKENS[i]` names the variable `getThemeCategoricalColors()`
 * reads at index `i`.
 *
 * This is the single source of truth for "slot i ↔ colour", and it exists so the
 * caller that assigns slots (a table's column-level colour map) and the charts that
 * read the palette cannot disagree. The length is taken from `getDefaultColors()`
 * rather than written as `12`, so adding a thirteenth slot to the ramp extends this
 * list instead of silently capping it — and the array is index-aligned with the
 * variables by construction there.
 *
 * References, not resolved colours: the browser resolves them per theme, so one
 * stored value is right in light and dark and a persisted board never freezes
 * today's palette. No DOM is needed to build this, which is what lets a server
 * render assign slots.
 */
export const CATEGORY_COLOR_TOKENS: ReadonlyArray<string> = getDefaultColors().map(
  (_, index) => `var(--color-viz-categorical-${index + 1})`
);
